import template from './uc-element-sorter.html.twig';
import './uc-element-sorter.scss';

/**
 * uc-element-sorter
 *
 * Drag-and-drop list that controls the render order of cart elements.
 * Emits `update:value` with the new JSON string whenever the order
 * or a toggle changes, so the parent can write it back to systemConfig.
 *
 * Props:
 *   value        – JSON string array of element keys (current order)
 *   disabledKeys – JSON string array of keys that are disabled
 */
Shopware.Component.register('uc-element-sorter', {
    template,

    props: {
        value: {
            type: String,
            default: '["trust-bar","usp-icons","timer","progress-bar","gift-offers","line-items","cross-sell","discount","savings-banner","order-summary","custom-html","custom-html-2","custom-html-3"]',
        },
        disabledKeys: {
            type: String,
            default: '[]',
        },
    },

    emits: ['update:value', 'update:disabledKeys'],

    data() {
        return {
            items: [],
            disabled: [],
            dragIndex: null,
            dragOverIndex: null,

            // All possible element keys (in case order JSON is missing some)
            allKeys: [
                'trust-bar',
                'usp-icons',
                'timer',
                'progress-bar',
                'gift-offers',
                'line-items',
                'discount',
                'savings-banner',
                'order-summary',
                'cross-sell',
                'custom-html',
                'custom-html-2',
                'custom-html-3',
            ],

            icons: {
                'trust-bar':      'regular-shield',
                'usp-icons':      'regular-star',
                'timer':          'regular-clock',
                'progress-bar':   'regular-circle',
                'gift-offers':    'regular-gift',
                'line-items':     'regular-shopping-bag',
                'discount':       'regular-tag',
                'savings-banner': 'regular-tag',
                'order-summary':  'regular-circle',
                'cross-sell':     'regular-tag',
                'custom-html':    'regular-code',
                'custom-html-2':  'regular-code',
                'custom-html-3':  'regular-code',
            },
        };
    },

    created() {
        this._parseProps();
    },

    watch: {
        value()       { this._parseProps(); },
        disabledKeys() { this._parseProps(); },
    },

    methods: {
        _parseProps() {
            try {
                const order = (JSON.parse(this.value) ?? [])
                    .filter(k => this.allKeys.includes(k)); // drop removed/unknown keys
                // Merge: order first, then any missing keys appended
                const all = [...order, ...this.allKeys.filter(k => !order.includes(k))];
                this.items = all;
            } catch {
                this.items = [...this.allKeys];
            }
            try {
                this.disabled = JSON.parse(this.disabledKeys) ?? [];
            } catch {
                this.disabled = [];
            }
        },

        label(key) {
            return this.$tc(`sellops-ultimate-cart.elements.${key}`);
        },

        icon(key) {
            return this.icons[key] ?? 'regular-circle';
        },

        isEnabled(key) {
            return !this.disabled.includes(key);
        },

        toggleEnabled(key) {
            if (this.disabled.includes(key)) {
                this.disabled = this.disabled.filter(k => k !== key);
            } else {
                this.disabled = [...this.disabled, key];
            }
            this.$emit('update:disabledKeys', JSON.stringify(this.disabled));
        },

        // ── Drag & Drop ───────────────────────────────────────────────────────

        onDragStart(index, event) {
            this.dragIndex = index;
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('text/plain', index);
        },

        onDragOver(index, event) {
            event.preventDefault();
            event.dataTransfer.dropEffect = 'move';
            this.dragOverIndex = index;
        },

        onDragLeave() {
            this.dragOverIndex = null;
        },

        onDrop(targetIndex) {
            if (this.dragIndex === null || this.dragIndex === targetIndex) {
                this.dragOverIndex = null;
                return;
            }

            const newItems = [...this.items];
            const [moved] = newItems.splice(this.dragIndex, 1);
            newItems.splice(targetIndex, 0, moved);
            this.items = newItems;

            this.dragIndex = null;
            this.dragOverIndex = null;

            this.$emit('update:value', JSON.stringify(this.items));
        },

        onDragEnd() {
            this.dragIndex = null;
            this.dragOverIndex = null;
        },

        // ── Manual reorder with arrow buttons (keyboard accessible) ──────────

        moveUp(index) {
            if (index === 0) return;
            const items = [...this.items];
            [items[index - 1], items[index]] = [items[index], items[index - 1]];
            this.items = items;
            this.$emit('update:value', JSON.stringify(this.items));
        },

        moveDown(index) {
            if (index >= this.items.length - 1) return;
            const items = [...this.items];
            [items[index], items[index + 1]] = [items[index + 1], items[index]];
            this.items = items;
            this.$emit('update:value', JSON.stringify(this.items));
        },
    },
});
